class CartThresholdManager {
  getThresholdConfig() {
    const thresholds = [];

    try {
      const firstAmount = document.querySelector('meta[name="first-threshold-amount"]')?.content;
      const firstProduct = document.querySelector('meta[name="first-threshold-gift-product"]')?.content;

      const secondAmount = document.querySelector('meta[name="second-threshold-amount"]')?.content;
      const secondProduct = document.querySelector('meta[name="second-threshold-gift-product"]')?.content;

      const thirdAmount = document.querySelector('meta[name="third-threshold-amount"]')?.content;
      const thirdProduct = document.querySelector('meta[name="third-threshold-gift-product"]')?.content;

      if (firstAmount && firstProduct && !isNaN(firstAmount) && !isNaN(firstProduct)) {
        const amount = parseInt(firstAmount);
        const variantId = parseInt(firstProduct);
        if (amount > 0 && variantId > 0) {
          thresholds.push({ amount: amount, variantId: variantId, level: 1 });
        }
      }

      if (secondAmount && secondProduct && !isNaN(secondAmount) && !isNaN(secondProduct)) {
        const amount = parseInt(secondAmount);
        const variantId = parseInt(secondProduct);
        if (amount > 0 && variantId > 0) {
          thresholds.push({ amount: amount, variantId: variantId, level: 2 });
        }
      }

      if (thirdAmount && thirdProduct && !isNaN(thirdAmount) && !isNaN(thirdProduct)) {
        const amount = parseInt(thirdAmount);
        const variantId = parseInt(thirdProduct);
        if (amount > 0 && variantId > 0) {
          thresholds.push({ amount: amount, variantId: variantId, level: 3 });
        }
      }

      return thresholds.sort((a, b) => a.amount - b.amount);
    } catch (error) {
      console.error("Error parsing threshold configuration:", error);
      return [];
    }
  }

  // ===== ARREGLO 4 (lectura de carrito SIEMPRE fresca) =====
  // Todas las lecturas de /cart.js pasan por aqui con cache: 'no-store'.
  // Antes, el navegador a veces devolvia un carrito viejo desde su cache y el
  // codigo creia que el termo NO estaba, agregandolo de nuevo (termo x2).
  async fetchCartFresh() {
    const response = await fetch(`${routes.cart_url}.js`, {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error("Failed to fetch cart");
    return response.json();
  }

  // ===== ARREGLO 5 (red de seguridad final) =====
  // Recorre el carrito y fuerza CUALQUIER linea de regalo (marcada con la
  // propiedad _threshold_gift o cuyo variant sea un producto de regalo) a
  // cantidad EXACTAMENTE 1. Se llama al final de cada revision, al abrir el
  // carrito y al cargar la pagina, de modo que un termo x2 se autocorrige
  // ANTES de que el cliente llegue a pagar.
  async normalizeAllGiftLines() {
    try {
      const thresholds = this.getThresholdConfig();
      const giftVariantIds = new Set(thresholds.map((t) => t.variantId));
      const cart = await this.fetchCartFresh();
      let changed = false;

      for (const item of cart.items) {
        const isGiftByProp =
          item.properties &&
          (item.properties._threshold_gift === "true" || item.properties._threshold_gift === true);
        const isGiftByVariant = giftVariantIds.has(item.variant_id);

        if ((isGiftByProp || isGiftByVariant) && item.quantity !== 1) {
          try {
            await this.setThresholdGiftQuantity(item.key, 1);
            changed = true;
            await new Promise((resolve) => setTimeout(resolve, 150));
          } catch (e) {
            console.error("Error normalizing gift line:", e);
          }
        }
      }
      return changed;
    } catch (error) {
      console.error("Error in normalizeAllGiftLines:", error);
      return false;
    }
  }

  async handleThresholdGifts(cartState) {
    try {
      const thresholds = this.getThresholdConfig();
      if (thresholds.length === 0) return false;

      const subtotal = cartState.total_price || 0;
      const cartItems = cartState.items || [];

      if (cartItems.length === 0) return false;

      const currentGiftItems = cartItems.filter((item) =>
        thresholds.some((threshold) => threshold.variantId === item.variant_id)
      );

      const regularItems = cartItems.filter(
        (item) => !thresholds.some((threshold) => threshold.variantId === item.variant_id)
      );

      if (regularItems.length === 0 && currentGiftItems.length > 0) {
        let giftsChanged = false;
        for (const giftItem of currentGiftItems) {
          try {
            await this.removeThresholdGift(giftItem.key);
            giftsChanged = true;
          } catch (removeError) {
            console.error("Error removing threshold gift:", removeError);
          }
        }
        return giftsChanged;
      }

      if (subtotal <= 0) {
        let giftsChanged = false;
        for (const giftItem of currentGiftItems) {
          try {
            await this.removeThresholdGift(giftItem.key);
            giftsChanged = true;
          } catch (removeError) {
            console.error("Error removing threshold gift:", removeError);
          }
        }
        return giftsChanged;
      }

      let highestMetThreshold = null;
      for (const threshold of thresholds) {
        if (subtotal >= threshold.amount) {
          if (!highestMetThreshold || threshold.amount > highestMetThreshold.amount) {
            highestMetThreshold = threshold;
          }
        }
      }

      const activeGifts = [];
      if (highestMetThreshold) {
        for (const threshold of thresholds) {
          if (threshold.amount <= highestMetThreshold.amount) {
            activeGifts.push(threshold);
          }
        }
      }

      let giftsChanged = false;

      // Normaliza/limpia los regalos existentes segun el estado inicial.
      for (const giftItem of currentGiftItems) {
        const shouldKeep = activeGifts.some((gift) => gift.variantId === giftItem.variant_id);

        if (!shouldKeep) {
          try {
            await this.removeThresholdGift(giftItem.key);
            giftsChanged = true;
          } catch (removeError) {
            console.error("Error removing threshold gift:", removeError);
          }
        } else if (giftItem.quantity !== 1) {
          try {
            await this.setThresholdGiftQuantity(giftItem.key, 1);
            giftsChanged = true;
          } catch (qtyError) {
            console.error("Error normalizing threshold gift quantity:", qtyError);
          }
        }
      }

      if (giftsChanged) {
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      try {
        // Lectura FRESCA (sin cache) para saber que regalos ya estan presentes.
        const currentCart = await this.fetchCartFresh();
        const currentGiftVariants = currentCart.items
          .filter((item) => thresholds.some((threshold) => threshold.variantId === item.variant_id))
          .map((item) => item.variant_id);

        const handledVariants = new Set(currentGiftVariants);
        for (const gift of activeGifts) {
          if (!handledVariants.has(gift.variantId)) {
            handledVariants.add(gift.variantId);
            try {
              await this.addThresholdGift(gift.variantId);
              giftsChanged = true;
              await new Promise((resolve) => setTimeout(resolve, 200));
            } catch (addError) {
              console.error("Error adding threshold gift:", addError);
            }
          }
        }
      } catch (cartFetchError) {
        console.error("Error fetching cart state for gift check:", cartFetchError);
      }

      // ===== ARREGLO 5 =====
      // Red de seguridad: pase lo que pase arriba, terminamos forzando cada
      // regalo a cantidad 1. Cierra la ventana de "se agrego dos veces".
      const normalized = await this.normalizeAllGiftLines();
      if (normalized) giftsChanged = true;

      return giftsChanged;
    } catch (error) {
      console.error("Error handling threshold gifts:", error);
      return false;
    }
  }

  async addThresholdGift(variantId) {
    if (!variantId || variantId <= 0) {
      console.error("Invalid variant ID for threshold gift:", variantId);
      return false;
    }

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: { _threshold_gift: "true" },
        }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error(`Failed to add threshold gift (${response.status}):`, errorData);
        return false;
      }

      await response.json();
      return true;
    } catch (error) {
      console.error("Error adding threshold gift to cart:", error);
      return false;
    }
  }

  async removeThresholdGift(itemKey) {
    if (!itemKey) {
      console.error("Invalid item key for threshold gift removal:", itemKey);
      return false;
    }

    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: itemKey, quantity: 0 }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error(`Failed to remove threshold gift (${response.status}):`, errorData);
        return false;
      }

      await response.json();
      return true;
    } catch (error) {
      console.error("Error removing threshold gift from cart:", error);
      return false;
    }
  }

  async setThresholdGiftQuantity(itemKey, quantity) {
    if (!itemKey) {
      console.error("Invalid item key for threshold gift quantity update:", itemKey);
      return false;
    }

    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: itemKey, quantity: quantity }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error(`Failed to set threshold gift quantity (${response.status}):`, errorData);
        return false;
      }

      await response.json();
      return true;
    } catch (error) {
      console.error("Error setting threshold gift quantity:", error);
      return false;
    }
  }
}

window.cartThresholdManager = new CartThresholdManager();

function setupThresholdAddToCartListeners() {
  if (window.thresholdListenersSetup) {
    return;
  }
  window.thresholdListenersSetup = true;

  document.addEventListener("click", (event) => {
    const quickAddButton = event.target.closest(
      '.quick-add__submit, button[name="add"][class*="quick-add"]'
    );
    if (quickAddButton && !quickAddButton.disabled) {
      setTimeout(() => runGlobalThresholdCheck("quick-add"), 1200);
    }
  });

  document.addEventListener("click", (event) => {
    const productFormButton = event.target.closest(
      '.product-form__submit, button[name="add"][id*="ProductSubmitButton"]'
    );
    if (productFormButton && productFormButton.closest("product-form") && !productFormButton.disabled) {
      setTimeout(() => runGlobalThresholdCheck("product-form"), 1200);
    }
  });

  // ===== ARREGLO 6 (autocuracion al abrir el carrito / actualizar) =====
  // Cualquier apertura o actualizacion del carrito dispara una normalizacion,
  // de modo que si por lo que sea quedo un termo x2, se corrige a 1 JUSTO antes
  // de que el cliente vea el carrito y pague.
  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
    try {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        setTimeout(() => runGlobalThresholdCheck("cart-update"), 600);
      });
    } catch (e) {}
  }
  document.addEventListener("click", (event) => {
    if (event.target.closest('[id^="lm-hd-cart-"], [id*="cart-icon"], .header__icon--cart, cart-drawer')) {
      setTimeout(() => window.cartThresholdManager.normalizeAllGiftLines(), 500);
    }
  });
}

let thresholdCheckInProgress = false;
let thresholdCheckQueued = false;

async function runGlobalThresholdCheck(source) {
  if (thresholdCheckInProgress) {
    thresholdCheckQueued = true;
    return;
  }
  thresholdCheckInProgress = true;

  try {
    if (!window.cartThresholdManager) return;

    const cartState = await window.cartThresholdManager.fetchCartFresh();

    const thresholdGiftsChanged = await window.cartThresholdManager.handleThresholdGifts(cartState);

    if (!thresholdGiftsChanged) return;

    if (document.querySelector("cart-drawer")) {
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`, { cache: "no-store" });
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, "text/html");
      const selectors = ["cart-drawer-items", ".cart-drawer__footer"];

      for (const selector of selectors) {
        const targetElement = document.querySelector(selector);
        const sourceElement = html.querySelector(selector);
        if (targetElement && sourceElement) {
          targetElement.replaceWith(sourceElement);
        }
      }
    }

    const mainCartItems = document.getElementById("main-cart-items");
    if (mainCartItems) {
      const response = await fetch(`${routes.cart_url}?section_id=main-cart-items`, { cache: "no-store" });
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, "text/html");
      const sourceQty = html.querySelector("cart-items");
      if (sourceQty) {
        mainCartItems.querySelector("cart-items").innerHTML = sourceQty.innerHTML;
      }
    }

    try {
      const cartIconResponse = await fetch(`${routes.cart_url}?section_id=cart-icon-bubble`, {
        cache: "no-store",
      });
      const cartIconText = await cartIconResponse.text();
      const cartIconHtml = new DOMParser().parseFromString(cartIconText, "text/html");
      const cartIconTarget = document.querySelector("#cart-icon-bubble");
      const cartIconSource = cartIconHtml.querySelector(".shopify-section");
      if (cartIconTarget && cartIconSource) {
        cartIconTarget.innerHTML = cartIconSource.innerHTML;
      }
    } catch (iconError) {
      console.error("Error updating cart icon:", iconError);
    }
  } catch (error) {
    console.error("Error in global threshold check:", error);
  } finally {
    thresholdCheckInProgress = false;
    if (thresholdCheckQueued) {
      thresholdCheckQueued = false;
      runGlobalThresholdCheck(source);
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  setupThresholdAddToCartListeners();
  // ===== ARREGLO 6 =====
  // Al cargar cualquier pagina, normaliza regalos que hayan quedado en x2 de
  // una sesion anterior, antes de que el cliente pague.
  setTimeout(() => {
    if (window.cartThresholdManager) window.cartThresholdManager.normalizeAllGiftLines();
  }, 1500);
});